import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, signal, computed } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CompanyContextService, NavCompanyItem } from '../../core/services/company-context.service';
import { SessionService } from '../../core/services/session.service';
import { AuthService } from '../../features/auth/data/services/auth.service';

@Component({
  selector: 'app-private-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, ReactiveFormsModule],
  templateUrl: './private-layout.html',
  styleUrl: './private-layout.scss'
})
export class PrivateLayout {
  private readonly companyContextService = inject(CompanyContextService);
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly companies = this.companyContextService.companies;
  readonly selectedCompanyName = this.companyContextService.selectedCompanyName;
  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;

  readonly isSuperAdmin = this.sessionService.isSuperAdmin;
  readonly isCompanyAdmin = this.sessionService.isCompanyAdmin;
  readonly userFullName = computed(() => {
    const user = this.sessionService.user();
    if (user) {
      const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return full || 'Account';
    }
    return 'Account';
  });

  isCompanyDropdownOpen = signal(false);
  isUserDropdownOpen = signal(false);
  isLogoutModalOpen = signal(false);
  isChangePasswordModalOpen = signal(false);

  isChangingPassword = false;
  passwordError = '';
  passwordSuccess = '';

  passwordForm = this.fb.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(6)]],
    confirmNewPassword: ['', Validators.required]
  }, { validators: this.passwordMatchValidator });

  passwordMatchValidator(g: any) {
    return g.get('newPassword').value === g.get('confirmNewPassword').value
      ? null : { mismatch: true };
  }

  toggleCompanyDropdown(): void {
    if (!this.isSuperAdmin()) return;
    this.isCompanyDropdownOpen.update(value => !value);
    this.isUserDropdownOpen.set(false);
  }

  toggleUserDropdown(): void {
    this.isUserDropdownOpen.update(value => !value);
    this.isCompanyDropdownOpen.set(false);
  }

  selectCompany(company: NavCompanyItem): void {
    this.companyContextService.setSelectedCompany(company.id);
    this.isCompanyDropdownOpen.set(false);
  }

  openLogoutModal(): void {
    this.isLogoutModalOpen.set(true);
    this.isUserDropdownOpen.set(false);
  }

  closeLogoutModal(): void {
    this.isLogoutModalOpen.set(false);
  }

  openChangePasswordModal(): void {
    this.isChangePasswordModalOpen.set(true);
    this.isUserDropdownOpen.set(false);
    this.passwordForm.reset();
    this.passwordError = '';
    this.passwordSuccess = '';
  }

  closeChangePasswordModal(): void {
    this.isChangePasswordModalOpen.set(false);
  }

  onChangePasswordSubmit(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isChangingPassword = true;
    this.passwordError = '';
    this.passwordSuccess = '';

    const payload = {
      currentPassword: this.passwordForm.value.currentPassword || '',
      newPassword: this.passwordForm.value.newPassword || '',
      confirmNewPassword: this.passwordForm.value.confirmNewPassword || ''
    };

    this.authService.changePassword(payload).subscribe({
      next: (res) => {
        this.isChangingPassword = false;
        this.passwordSuccess = res.message || 'Password changed successfully.';
        this.passwordForm.reset();
        setTimeout(() => this.closeChangePasswordModal(), 2000);
      },
      error: (err) => {
        this.isChangingPassword = false;
        this.passwordError = err.error?.message || 'Failed to change password. Please check your current password.';
      }
    });
  }

  confirmLogout(): void {
    this.isLogoutModalOpen.set(false);
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  @HostListener('document:click')
  closeDropdown(): void {
    this.isCompanyDropdownOpen.set(false);
    this.isUserDropdownOpen.set(false);
  }

  onDropdownClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}